import React from 'react';
import { ContextualMenu, IconButton, IContextualMenuItem } from '@fluentui/react';

// Imperative handle so an uploader can keep its own click target (the image, the tile) and just
// ask the menu to open; the visuals of the host control stay exactly as they were.
export interface IAssetSourceMenuHandle {
    // `target` anchors the menu; omit it to anchor on the menu's own host element.
    open: (target?: HTMLElement | null) => void;
    close: () => void;
}

interface IAssetSourceMenuProps {
    // 'Select Asset…': the host opens the asset picker panel.
    onSelectAsset: () => void;
    // 'Upload New': the host clicks its existing hidden file input.
    onUploadNew: () => void;
    disabled?: boolean;
    // The small overlaid affordance. Turn it off when the host already has one.
    showChevron?: boolean;
    // Positioning for the host element. Two shapes are in use:
    //  - with children: a relative inline-block wrapper hugging the wrapped control (default), the
    //    chevron pinned to its bottom-right corner;
    //  - without children: pass an absolute position so the host is just a chevron overlay inside
    //    the caller's own already-relative container.
    wrapperStyle?: React.CSSProperties;
    chevronTitle?: string;
    children?: React.ReactNode;
}

const chevronStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 22,
    height: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)',
    borderRadius: 2,
};

const AssetSourceMenu = React.forwardRef<IAssetSourceMenuHandle, IAssetSourceMenuProps>((props, ref) => {
    const hostRef = React.useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [target, setTarget] = React.useState<HTMLElement | null>(null);

    React.useImperativeHandle(ref, () => ({
        open: (t?: HTMLElement | null) => {
            if (props.disabled) return;
            setTarget(t ?? hostRef.current);
            setIsOpen(true);
        },
        close: () => setIsOpen(false),
    }));

    const items: IContextualMenuItem[] = [
        {
            key: 'selectAsset',
            text: 'Select Asset…',
            iconProps: { iconName: 'Photo2' },
            onClick: () => {
                setIsOpen(false);
                props.onSelectAsset();
            },
        },
        {
            key: 'uploadNew',
            text: 'Upload New',
            iconProps: { iconName: 'Upload' },
            onClick: () => {
                setIsOpen(false);
                props.onUploadNew();
            },
        },
    ];

    const hasChildren = props.children !== undefined && props.children !== null;
    const defaultWrapperStyle: React.CSSProperties = hasChildren
        ? { position: 'relative', display: 'inline-block' }
        : { position: 'absolute', bottom: 2, right: 2 };

    return (
        <div ref={hostRef} style={{ ...defaultWrapperStyle, ...props.wrapperStyle }}>
            {props.children}
            {(props.showChevron ?? true) && (
                <IconButton
                    iconProps={{ iconName: 'ChevronDownSmall' }}
                    title={props.chevronTitle ?? 'Image options'}
                    ariaLabel={props.chevronTitle ?? 'Image options'}
                    disabled={props.disabled}
                    style={hasChildren ? chevronStyle : { ...chevronStyle, position: 'static', bottom: undefined, right: undefined }}
                    onClick={(e) => {
                        // The host control usually has its own click handler (open the file dialog,
                        // open the menu); this affordance must not trigger it as well.
                        e.preventDefault();
                        e.stopPropagation();
                        setTarget(e.currentTarget as HTMLElement);
                        setIsOpen(true);
                    }}
                />
            )}
            {isOpen && (
                <ContextualMenu
                    items={items}
                    target={target ?? hostRef.current}
                    onDismiss={() => setIsOpen(false)}
                />
            )}
        </div>
    );
});

AssetSourceMenu.displayName = 'AssetSourceMenu';

export default AssetSourceMenu;
